import { useCallback, useEffect, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { StarfieldCanvas } from "@/components/StarfieldCanvas";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { TestimonialSlider } from "@/components/TestimonialSlider";
import { Mythology } from "@/components/Mythology";
import { MeetMara } from "@/components/MeetMara";
import { FreeRecognition } from "@/components/FreeRecognition";
import { TruthSection } from "@/components/TruthSection";
import { UniqueSection } from "@/components/UniqueSection";
import { Readings } from "@/components/Readings";
import { Whispers } from "@/components/Whispers";
import { ShopSection } from "@/components/ShopSection";
import { BookingForm } from "@/components/BookingForm";
import { Footer } from "@/components/Footer";
import { LaunchStardust } from "@/components/LaunchStardust";
import { IntakeOverlay } from "@/components/IntakeOverlay";
import { BackRoom } from "@/components/BackRoom";
import { FloatingChatButton } from "@/components/FloatingChatButton";
import { ConsentBanner } from "@/components/ConsentBanner";
import { MaraConsole } from "@/components/MaraConsole";
import { TermsPage } from "@/components/TermsPage";
import { PrivacyPage } from "@/components/PrivacyPage";
import { PromoBanner } from "@/components/PromoBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
    const [selectedReading, setSelectedReading] = useState("");
    const [overlay, setOverlay] = useState(null);
    const [conversationId, setConversationId] = useState(() => localStorage.getItem("ds_conversation") || "");
    const isMaraRoute = window.location.pathname === "/mara";
    const isTermsRoute = window.location.pathname === "/terms";
    const isPrivacyRoute = window.location.pathname === "/privacy";

    useEffect(() => {
        if (isMaraRoute) return;
        const lenis = new Lenis({ anchors: true, lerp: 0.09 });
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, [isMaraRoute]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("payment") === "cancelled") {
            toast.info("The payment mirror closed before completion. No time was taken, nothing was charged.");
            window.history.replaceState({}, "", "/");
            return;
        }
        const token = params.get("token");
        if (params.get("payment") !== "return" || !token) return;
        const poll = async () => {
            try {
                const res = await axios.post(`${API}/payments/paypal/capture`, { order_id: token });
                if (res.data.payment_status === "paid") {
                    if (res.data.kind === "product") {
                        toast.success("YOUR ORDER IS WITH MARA — she will write to you when your parcel is sealed and sent.");
                    } else {
                        toast.success("YOUR READING IS SEALED — your minutes wait for you in the Back Room.");
                        if (localStorage.getItem("ds_conversation")) setOverlay("backroom");
                    }
                    window.history.replaceState({}, "", "/");
                }
            } catch {
                toast.error("The payment mirror clouded — if you paid, Mara will confirm shortly.");
                window.history.replaceState({}, "", "/");
            }
        };
        poll();
    }, []);

    const bookReading = (id) => {
        setSelectedReading(id);
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
    };

    const openRecognition = useCallback(() => {
        setOverlay(conversationId ? "backroom" : "intake");
    }, [conversationId]);

    const handleIntakeDone = (cid) => {
        localStorage.setItem("ds_conversation", cid);
        setConversationId(cid);
        setOverlay("backroom");
    };

    const closeBackRoom = (reset) => {
        if (reset) {
            localStorage.removeItem("ds_conversation");
            setConversationId("");
        }
        setOverlay(null);
    };

    if (isMaraRoute || isTermsRoute || isPrivacyRoute) {
        return (
            <div className="App">
                <StarfieldCanvas />
                {isMaraRoute ? <MaraConsole /> : isTermsRoute ? <TermsPage /> : <PrivacyPage />}
                <Toaster
                    position="bottom-center"
                    toastOptions={{
                        style: { background: "#18002e", border: "1px solid rgba(199,125,255,0.4)", color: "#f8f7ff" },
                    }}
                />
            </div>
        );
    }

    return (
        <div className="App">
            <StarfieldCanvas />
            <Header onSpeak={openRecognition} />
            <PromoBanner onBegin={openRecognition} />
            <main>
                <Hero onBegin={openRecognition} />
                <Marquee />
                <TestimonialSlider />
                <Mythology />
                <MeetMara onBegin={openRecognition} />
                <FreeRecognition onBegin={openRecognition} />
                <TruthSection onBegin={openRecognition} />
                <UniqueSection onEnter={handleIntakeDone} />
                <Readings onSelect={bookReading} onBegin={openRecognition} />
                <Whispers />
                <ShopSection />
                <BookingForm selectedReading={selectedReading} onReadingChange={setSelectedReading} />
            </main>
            <Footer />
            <LaunchStardust />
            <FloatingChatButton onOpen={openRecognition} hidden={overlay === "backroom"} />
            <ConsentBanner />
            <AnimatePresence>
                {overlay === "intake" && (
                    <IntakeOverlay onClose={() => setOverlay(null)} onEnter={handleIntakeDone} />
                )}
            </AnimatePresence>
            {overlay === "backroom" && conversationId && (
                <BackRoom conversationId={conversationId} onClose={closeBackRoom} />
            )}
            <Toaster
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#18002e",
                        border: "1px solid rgba(199,125,255,0.4)",
                        color: "#f8f7ff",
                    },
                }}
            />
        </div>
    );
}

export default App;
